import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SeenLogo, SeenIcon } from '@/components/common/SeenLogo';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { 
  Scan, 
  Upload, 
  Shield, 
  Users, 
  AlertTriangle, 
  CheckCircle,
  ArrowRight,
  Eye,
  Sparkles,
  Building2,
  Code,
  Heart
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: Scan,
      title: 'Barcode Scanner',
      description: 'Instantly scan product barcodes with your camera to reveal all ingredients.',
      color: 'bg-[#5DBDB3]/10 text-[#5DBDB3]'
    },
    {
      icon: Upload,
      title: 'Image Analysis',
      description: 'Upload photos of ingredient labels for AI-powered analysis.',
      color: 'bg-[#F08B7D]/10 text-[#F08B7D]'
    },
    {
      icon: Eye,
      title: 'Hidden Ingredients',
      description: 'Discover disguised additives hiding behind marketing names.',
      color: 'bg-[#5DBDB3]/10 text-[#5DBDB3]'
    },
    {
      icon: Shield,
      title: 'Allergen Alerts',
      description: 'Set your allergens and get instant warnings on every scan.',
      color: 'bg-[#F08B7D]/10 text-[#F08B7D]'
    },
    {
      icon: Sparkles,
      title: 'Health Scoring',
      description: 'Get an instant health score based on ingredient quality.',
      color: 'bg-[#5DBDB3]/10 text-[#5DBDB3]'
    },
    {
      icon: Users,
      title: 'Community Reports',
      description: 'Learn from community-reported hidden ingredients.',
      color: 'bg-[#F08B7D]/10 text-[#F08B7D]'
    },
  ];

  const howItWorks = [
    { step: '01', title: 'Scan or Upload', description: 'Use your camera or upload a photo of the ingredient label' },
    { step: '02', title: 'AI Analysis', description: 'Our AI analyzes every ingredient, including hidden ones' },
    { step: '03', title: 'Get Insights', description: 'See health scores, warnings, and detailed breakdowns' },
  ];

  const businessFeatures = [
    { icon: Building2, title: 'For Companies', desc: 'Bulk analysis & team management' },
    { icon: Code, title: 'API Access', desc: 'Integrate into your apps' },
    { icon: Users, title: 'Team Plans', desc: 'Collaborate with your team' },
    { icon: Shield, title: 'White-Label', desc: 'Custom branded reports' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <SeenIcon size={32} />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/about">
                <Button variant="ghost" className="text-foreground">{t('about.title')}</Button>
              </Link>
              <Link to="/auth">
                <Button variant="ghost" className="text-foreground" data-testid="landing-login-btn">{t('common.login')}</Button>
              </Link>
              <Link to="/auth">
                <Button className="rounded-full px-6 bg-[#5DBDB3] hover:bg-[#4DA9A0]" data-testid="landing-signup-btn">
                  {t('landing.hero.cta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              {/* Logo */}
              <SeenLogo size="xl" showTagline />
              
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.1]" data-testid="hero-title">
                {t('landing.hero.title')}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                {t('landing.hero.subtitle')}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/auth">
                  <Button size="lg" className="rounded-full px-8 py-6 text-base bg-[#5DBDB3] hover:bg-[#4DA9A0] btn-press" data-testid="hero-cta-btn">
                    {t('landing.hero.cta')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base border-[#5DBDB3] text-[#5DBDB3] hover:bg-[#5DBDB3]/10"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('landing.hero.secondaryCta')}
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#5DBDB3]" />
                  <span className="text-sm text-muted-foreground">AI-Powered Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#5DBDB3]" />
                  <span className="text-sm text-muted-foreground">100% Free</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-slide-in-right hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1610210143623-417eda0cca0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMHZlZ2V0YWJsZXMlMjBmcnVpdHMlMjBpbmdyZWRpZW50cyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzczMTMyOTE3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Fresh ingredients"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg animate-slide-up stagger-3 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#5DBDB3]/10 flex items-center justify-center">
                    <span className="text-xl font-bold text-[#5DBDB3]">92</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Health Score</p>
                    <p className="text-xs text-muted-foreground">Excellent</p>
                  </div>
                </div>
              </div>

              {/* Warning card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg animate-slide-up stagger-2 border border-[#F08B7D]/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#F08B7D]" />
                  <span className="text-sm font-medium">Hidden MSG detected</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-[#F08B7D]" style={{
          clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 75% 50%, 50% 0, 25% 50%, 0 0)'
        }} />
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="features-title">
              Everything You Need to <span className="text-[#5DBDB3]">Know</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive tools to understand what&apos;s really in your food
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={feature.title} 
                className="border border-border bg-card hover:border-[#5DBDB3]/50 transition-all duration-200 hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-6`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                How It <span className="text-[#F08B7D]">Works</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-12">
                Get instant insights in three simple steps
              </p>

              <div className="space-y-8">
                {howItWorks.map((item, index) => (
                  <div 
                    key={item.step} 
                    className="flex gap-6 items-start animate-slide-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`how-it-works-${index}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#5DBDB3] flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/6435ff9c-622f-462c-9a37-2e6118076a7c/images/a8c4e807c89db2a13e4c848f83c3edea7d3970eb4cef059b603d7db4d6749a3f.png"
                alt="Hand scanning food product barcode with smartphone at home"
                className="rounded-2xl shadow-2xl w-full object-cover h-[500px]"
              />
              {/* Teal border accent */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-[#5DBDB3] rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist - Andy's Story Teaser */}
      <section className="py-20 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4 border-[#F08B7D] text-[#F08B7D]">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Why We Exist
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="text-[#F08B7D]">10%</span> of People Have Food Allergies.
                <br />
                <span className="text-[#5DBDB3]">All</span> Deserve Protection.
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our founder has lived with a legumes allergy for over four decades. Because it falls outside the 14 mandatory allergens, it <strong>never appears on product labels</strong>.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                This gap in labelling regulations inspired the creation of <strong className="text-[#5DBDB3]">Seen Beyond the Label</strong> — ensuring comprehensive ingredient transparency for <em>all</em> allergies, not just the common ones.
              </p>
              <Link to="/about">
                <Button variant="outline" className="rounded-full border-[#5DBDB3] text-[#5DBDB3] hover:bg-[#5DBDB3] hover:text-white">
                  Read Our Story
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <Card className="border-2 border-[#5DBDB3]/20 bg-gradient-to-br from-[#5DBDB3]/5 to-[#F08B7D]/5">
                <CardContent className="p-8">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-[#F08B7D]/10 flex items-center justify-center mx-auto mb-6">
                      <AlertTriangle className="w-10 h-10 text-[#F08B7D]" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4">The Labelling Gap</h3>
                    <div className="space-y-3 text-left">
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                        <span className="text-2xl font-bold text-[#5DBDB3]">14</span>
                        <span className="text-sm text-muted-foreground">Allergens legally required on labels</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                        <span className="text-2xl font-bold text-[#F08B7D]">10%</span>
                        <span className="text-sm text-muted-foreground">Of global population affected by food allergies</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                        <span className="text-2xl font-bold text-[#5DBDB3]">∞</span>
                        <span className="text-sm text-muted-foreground">Allergies we support at SEEN</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Hardship Access - Free for Those in Need */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#F08B7D]/10 via-white to-[#F08B7D]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-[#F08B7D]/30 bg-white shadow-lg overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-5 items-center">
                {/* Heart Icon Section */}
                <div className="md:col-span-1 bg-[#F08B7D]/10 p-8 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#F08B7D] flex items-center justify-center">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="md:col-span-4 p-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                      <Badge variant="outline" className="mb-3 border-[#F08B7D] text-[#F08B7D]">
                        <Heart className="w-3 h-3 mr-1" />
                        Free Access Available
                      </Badge>
                      <h3 className="text-2xl font-bold mb-2">
                        On Benefits or Low Income?
                      </h3>
                      <p className="text-muted-foreground">
                        Everyone deserves to know what&apos;s in their food. We offer <strong className="text-[#5DBDB3]">completely free</strong> access 
                        to all premium features — including hidden ingredients detection — for those on Universal Credit, 
                        PIP, or experiencing financial hardship. <span className="text-[#F08B7D] font-medium">Simply upload proof of benefits.</span>
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Link to="/subscription">
                        <Button 
                          className="bg-[#F08B7D] hover:bg-[#e07a6c] text-white rounded-full px-6"
                          data-testid="hardship-cta-btn"
                        >
                          <Heart className="w-4 h-4 mr-2" />
                          Apply for Free Access
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#5DBDB3]/5 to-[#F08B7D]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
              For <span className="text-[#5DBDB3]">Businesses</span> & Food Chains
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Scale your ingredient transparency with enterprise features
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {businessFeatures.map((item, index) => (
              <Card key={index} className="border border-border text-center p-6 hover:border-[#5DBDB3]/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#5DBDB3]" />
                </div>
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/subscription">
              <Button size="lg" variant="outline" className="rounded-full px-8 border-[#5DBDB3] text-[#5DBDB3] hover:bg-[#5DBDB3] hover:text-white">
                View Business Plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#5DBDB3] text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F08B7D] rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <SeenIcon size={64} className="mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
            Ready to See Beyond the Label?
          </h2>
          <p className="text-lg opacity-90 mb-10 max-w-2xl mx-auto">
            Join thousands of health-conscious consumers making informed choices every day.
          </p>
          <Link to="/auth">
            <Button 
              size="lg" 
              className="rounded-full px-10 py-6 text-base bg-white text-[#5DBDB3] hover:bg-white/90 btn-press"
              data-testid="cta-btn"
            >
              Start Free Today
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer with Legal Links */}
      <Footer />
    </div>
  );
};

export default LandingPage;
